import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import type { NavItemId } from '@components/shell/BottomFloatingNav'

/** Which shell wraps the route — drives layout chrome (nav vs back-arrow). */
export type RouteShell = 'app' | 'detail' | 'none'

/**
 * Single source of truth for every page route in the mobile app. The
 * router (`src/routes/index.tsx`) groups these by `shell` and `requiresAuth`
 * to build the actual nested `RouteObject[]`. Adding a new page is a
 * single-array-entry change.
 */
export interface AppRouteConfig {
  /** Path pattern (React Router syntax — supports `:params`). */
  path: string
  /** Lazy-loaded page component (default export). */
  component: LazyExoticComponent<ComponentType>
  /**
   * Layout chrome to wrap the page in:
   *   - `'app'`    → HomeHeader + main + BottomFloatingNav
   *   - `'detail'` → Back-button header + main (no bottom nav)
   *   - `'none'`   → No chrome at all (e.g. `/login`)
   */
  shell: RouteShell
  /** Page title. Appended with `" · ORO"` in the document title. */
  title: string
  /**
   * If set, BottomFloatingNav highlights this nav id when the route is
   * active. Omit for routes that don't correspond to a tab (detail
   * pages, login).
   */
  navItem?: NavItemId
  /**
   * When `false`, the route is publicly accessible (e.g. `/login`).
   * When `true` (default), the route is wrapped in `AuthGuard`.
   */
  requiresAuth: boolean
  /**
   * Where the back button on a detail-shell page navigates to. Falls
   * back to `navigate(-1)` when omitted, but supplying an explicit
   * parent path keeps the back button working when the user lands on
   * the detail URL directly (no history).
   */
  parentPath?: string
}

/**
 * Master route table — order is irrelevant for routing (paths match by
 * pattern), but kept loosely grouped by destination for readability.
 */
export const appRoutes: AppRouteConfig[] = [
  // --- Public ---------------------------------------------------------------
  {
    path: '/login',
    component: lazy(() => import('@pages/Login/LoginPage')),
    shell: 'none',
    title: 'Sign in',
    requiresAuth: false,
  },
  // process-runner's `/:tenantId` catch-all — scoped to `/login/:tenantId`
  // so it doesn't shadow other top-level routes (mobile has more public
  // paths than the web app). The login page reads `params.tenantId` and
  // pre-fills the tenant input.
  {
    path: '/login/:tenantId',
    component: lazy(() => import('@pages/Login/LoginPage')),
    shell: 'none',
    title: 'Sign in',
    requiresAuth: false,
  },
  {
    path: '/otp',
    component: lazy(() => import('@pages/Login/OtpPage')),
    shell: 'none',
    title: 'One-time code',
    requiresAuth: false,
  },
  {
    path: '/initforgetpwd',
    component: lazy(() => import('@pages/Login/InitiateForgetPasswordPage')),
    shell: 'none',
    title: 'Reset password',
    requiresAuth: false,
  },
  {
    path: '/forgotpwd/:userName/:tenantId?',
    component: lazy(() => import('@pages/Login/ResetPasswordPage')),
    shell: 'none',
    title: 'Reset password',
    requiresAuth: false,
  },
  {
    path: '/confirmsms/:userName/:ambiguous/:sessionId/:tenantId?',
    component: lazy(() => import('@pages/Login/ConfirmSmsPage')),
    shell: 'none',
    title: 'Verify code',
    requiresAuth: false,
  },
  {
    path: '/changepwd/:userName/:ambiguous/:sessionId/:tenantId?',
    component: lazy(() => import('@pages/Login/ChangePasswordPage')),
    shell: 'none',
    title: 'Set new password',
    requiresAuth: false,
  },
  {
    path: '/mfasetup/:userName/:ambiguous/:sessionId/:tenantId?',
    component: lazy(() => import('@pages/Login/MfaSetupPage')),
    shell: 'none',
    title: 'MFA setup',
    requiresAuth: false,
  },
  {
    path: '/callback',
    component: lazy(() => import('@pages/Login/CallbackPage')),
    shell: 'none',
    title: 'Signing in…',
    requiresAuth: false,
  },
  {
    path: '/logout',
    component: lazy(() => import('@pages/Login/LogoutPage')),
    shell: 'none',
    title: 'Signed out',
    requiresAuth: false,
  },
  {
    path: '/profile-setup',
    component: lazy(() => import('@pages/Login/ProfileSetupPage')),
    shell: 'none',
    title: 'Profile setup',
    requiresAuth: false,
  },

  // --- App-shell (list pages with bottom nav) -------------------------------
  // `/` and `/tasks` render the same component; the canonical URL is /tasks
  // (the bottom nav links to /tasks), but `/` is supported as the root entry
  // point per the user spec.
  // Login isn't implemented yet — `AuthGuard` is a bare passthrough today,
  // so `requiresAuth: true` still keeps both `/` and `/tasks` openly
  // accessible while preserving the AppShell wrapping.
  {
    path: '/',
    component: lazy(() => import('@pages/Tasks/TaskListPage')),
    shell: 'app',
    title: 'Tasks',
    navItem: 'home',
    requiresAuth: true,
  },
  {
    path: '/tasks',
    component: lazy(() => import('@pages/Tasks/TaskListPage')),
    shell: 'app',
    title: 'Tasks',
    navItem: 'home',
    requiresAuth: true,
  },
  // Login isn't implemented yet — `AuthGuard` is a bare passthrough today,
  // so `/engagements` stays openly accessible while keeping `requiresAuth: true`
  // for when auth lands.
  {
    path: '/engagements',
    component: lazy(() => import('@pages/Requests/RequestsPage')),
    shell: 'app',
    title: 'Active requests',
    navItem: 'requests',
    requiresAuth: true,
  },
  // Login isn't implemented yet — `AuthGuard` is a bare passthrough today,
  // so `/inbox` stays openly accessible while keeping `requiresAuth: true`
  // for when auth lands.
  {
    path: '/inbox',
    component: lazy(() => import('@pages/Inbox/InboxPage')),
    shell: 'app',
    title: 'Inbox',
    navItem: 'inbox',
    requiresAuth: true,
  },

  // --- Detail-shell (back-nav pages, no bottom nav) -------------------------
  {
    path: '/task/:engagementId/:taskId',
    component: lazy(() => import('@pages/Tasks/TaskDetailPage')),
    shell: 'detail',
    title: 'Task',
    parentPath: '/tasks',
    requiresAuth: true,
  },
  {
    path: '/engagement/:engagementId',
    component: lazy(() => import('@pages/Requests/RequestDetailPage')),
    shell: 'detail',
    title: 'Request',
    parentPath: '/engagements',
    requiresAuth: true,
  },
  {
    path: '/engagement/:engagementId/messages',
    component: lazy(() => import('@pages/Requests/MessageThreadPage')),
    shell: 'detail',
    title: 'Messages',
    // Note: ideally `parentPath` would resolve back to `/engagement/:engagementId`,
    // but that requires the live `:engagementId` value at render time. The
    // detail shell falls back to `navigate(-1)` for parametric parents.
    requiresAuth: true,
  },
]
